import React, { useEffect, useCallback } from 'react';
import { WorkflowEditor } from '@/components/workflow/WorkflowEditor';
import { WorkflowProvider, useWorkflow } from '@/contexts/WorkflowContext';
import { Flow } from '@/types/Document';

interface WorkflowResultsPaneProps {
  initialResults: Flow[];
  warnings?: string[];
  onSave: (updated: Flow[]) => Promise<void>;
  onSendToSiteflow?: (flows: Flow[], projectId: string, familyId: string) => Promise<void>;
  sendingToSiteflow?: boolean;
  siteflowError?: string;
  siteflowMessage?: string;
}

export default function WorkflowResultsPane({ initialResults, warnings, onSave, onSendToSiteflow, sendingToSiteflow, siteflowError, siteflowMessage }: WorkflowResultsPaneProps) {
  const Bootstrapper: React.FC<{ onSaveInner: (flows: Flow[]) => Promise<void> }> = ({ onSaveInner }) => {
    const { setWorkflows, setCurrentFlow, workflows, hasUnsavedChanges, saveChanges } = useWorkflow();
    useEffect(() => {
      setWorkflows(initialResults as any);
      if (initialResults && initialResults.length > 0) {
        setCurrentFlow((initialResults as any)[0]);
      }
    }, [initialResults, setWorkflows, setCurrentFlow]);
    useEffect(() => {
      // no-op here; saving is triggered by header button in panel invoking context saveChanges
    }, [workflows, hasUnsavedChanges, saveChanges]);
    return null;
  };

  const handleSave = useCallback(async (flows: Flow[]) => {
    await onSave(flows);
  }, [onSave]);

  return (
    <WorkflowProvider onSave={async (wf) => { await handleSave(wf as any); }}>
      <div className="w-full h-full flex flex-col">
        {/* Warnings Banner */}
        {warnings && warnings.length > 0 && (
          <div className="mx-2 mt-2 mb-0 rounded-md bg-amber-50 border border-amber-300 p-3 flex-shrink-0">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-amber-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-amber-800">
                  Processing completed with {warnings.length === 1 ? 'a warning' : `${warnings.length} warnings`}
                </h3>
                <div className="mt-1 text-sm text-amber-700">
                  <ul className={warnings.length > 1 ? 'list-disc pl-5 space-y-1' : ''}>
                    {warnings.map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
        <WorkflowEditor
          sendingToSiteflow={!!sendingToSiteflow}
          siteflowError={siteflowError}
          siteflowMessage={siteflowMessage}
          onSendToSiteflow={onSendToSiteflow ? (projectId: string, familyId: string) => onSendToSiteflow(initialResults as any, projectId, familyId) : undefined}
        />
        <Bootstrapper onSaveInner={handleSave} />
      </div>
    </WorkflowProvider>
  );
}


